/**
 * Settings > Gates > New gate / Edit gate (SAM-946, mock Gates v3): a full page, not a drawer. Name and code are
 * fixed once the gate exists (Invitation Types point at the name in their gate rules); only the type can change.
 * An internal gate picks its zone from the tenant's catalog (G3/G4 v4), once the catalog exists (GATE_ZONE_ENABLED).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { useCreateGate, useGateTypes, useGates, useUpdateGate } from '../../../hooks/useGates';
import { useInvitationTypes } from '../../../hooks/useInvitationTypes';
import { useZones } from '../../../hooks/useZones';
import { Notice } from '../../../components/ui/Notice';
import { toaster } from '../../../components/ui/toast';
import {
  EMPTY_GATE_FORM, GATE_ZONE_ENABLED, gateIssues, gatePayload, gateSaveFailure, gateTypeOptions, invitationTypesUsing, normalizeCode,
  toGateRows, usageText, type GateForm, type GateKind,
} from './gatesModel';

const KIND_GROUPS: Array<{ id: GateKind; label: string; description: string }> = [
  { id: 'external', label: 'External', description: 'Entrance from outside the community' },
  { id: 'internal', label: 'Internal', description: 'Inside the community, like a residential area' },
];

const fieldBox = 'h-11 rounded-lg border border-solid px-3 text-[15px]';

const Locked: React.FC<{ id: string; value: string; mono?: boolean }> = ({ id, value, mono }) => (
  <div className="flex h-11 items-center justify-between gap-2 rounded-lg border border-solid border-[var(--line)] bg-[var(--surface)] px-3">
    <input
      id={id}
      value={value}
      readOnly
      className={`min-w-0 border-0 bg-transparent text-[15px] text-[var(--ink-700)] outline-none ${mono ? 'font-mono' : ''}`}
    />
    <Lock size={16} className="shrink-0 text-[var(--ink-500)]" aria-hidden />
  </div>
);

export const GateFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const editingId = id ? Number(id) : undefined;
  const isEdit = editingId !== undefined;

  const { data: gates = [], isLoading: loadingGates, isError } = useGates();
  const { data: types = [], isLoading: loadingTypes } = useGateTypes();
  const { invitationTypes } = useInvitationTypes();
  const { data: zones = [] } = useZones(GATE_ZONE_ENABLED);
  const create = useCreateGate();
  const update = useUpdateGate();

  const rows = useMemo(() => toGateRows(gates, types), [gates, types]);
  const current = isEdit ? rows.find((r) => r.id === editingId) : undefined;

  const [form, setForm] = useState<GateForm>(EMPTY_GATE_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [serverCodeIssue, setServerCodeIssue] = useState<string | null>(null);
  useEffect(() => {
    if (current) setForm({ name: current.name, code: current.code, typeId: current.typeId ?? null, zoneId: current.zoneId });
  }, [current?.id, current?.typeId, current?.zoneId]); // eslint-disable-line react-hooks/exhaustive-deps

  const issues = gateIssues(form, rows, editingId);
  const typeOptions = useMemo(() => gateTypeOptions(types), [types]);
  const typesReady = typeOptions.length > 0;
  const kind = typeOptions.find((t) => t.id === form.typeId)?.kind ?? null;
  const showZone = GATE_ZONE_ENABLED && kind === 'internal';
  const dirty = isEdit ? form.typeId !== (current?.typeId ?? null) || (showZone && form.zoneId !== current?.zoneId) : true;
  const saving = create.isPending || update.isPending;
  const usage = isEdit && current ? usageText(invitationTypesUsing(current.name, invitationTypes)) : '';

  if (loadingGates || loadingTypes) {
    return (
      <div role="status" className="flex h-full items-center justify-center gap-2 text-sm text-[var(--ink-500)]">
        <Loader2 size={16} className="animate-spin" aria-hidden /> Loading…
      </div>
    );
  }
  if (isError || (isEdit && !current)) {
    return (
      <div className="h-full overflow-auto bg-[var(--surface)] p-4 md:p-8">
        <Notice variant="error">
          {isError ? 'Could not load the gates.' : 'This gate does not exist.'}{' '}
          <Link to="/settings/gates" className="font-semibold">Back to Gates</Link>
        </Notice>
      </div>
    );
  }

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (Object.keys(issues).length > 0 || !typesReady || saving) return;
    const data = gatePayload(form, isEdit, kind);
    setServerCodeIssue(null);
    const done = (message: string) => {
      toaster.positive(message, { autoHideDuration: 3000 });
      navigate('/settings/gates');
    };
    const failed = (err: unknown) => {
      const failure = gateSaveFailure(err, isEdit);
      if (failure.field === 'code') setServerCodeIssue(failure.message);
      else toaster.negative(failure.message, { autoHideDuration: 5000 });
    };
    if (editingId !== undefined) update.mutate({ id: editingId, data }, { onSuccess: () => done('Gate saved'), onError: failed });
    else create.mutate(data, { onSuccess: () => done('Gate created'), onError: failed });
  };

  return (
    <div className="h-full overflow-auto bg-[var(--surface)]">
      <main className="flex min-w-0 flex-col gap-5 px-4 pb-10 pt-4 md:px-8 md:pb-12 md:pt-7">
        <div className="flex flex-col gap-1">
          <span className="text-[13px] text-[var(--ink-500)]">
            <Link to="/settings/gates" className="text-[var(--ink-500)] no-underline">Settings › Gates</Link>
          </span>
          <h1 className="m-0 text-[26px] font-bold">{isEdit ? current?.name : 'New gate'}</h1>
        </div>

        <form
          onSubmit={save}
          noValidate
          className="flex max-w-[640px] flex-col gap-5 rounded-lg bg-white p-6 shadow-[var(--sh-1)]"
        >
          {!typesReady && (
            <Notice variant="warn">No gate types are set up for this tenant yet.</Notice>
          )}

          <div className="flex flex-col gap-1.5">
            <label htmlFor="gate-name" className="text-sm font-semibold">Name</label>
            {isEdit ? (
              <Locked id="gate-name" value={form.name} />
            ) : (
              <>
                <input
                  id="gate-name"
                  placeholder="Main gate"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  aria-invalid={submitted && !!issues.name}
                  className={`${fieldBox} border-[var(--ink-300)] text-[var(--ink-900)]`}
                />
                {submitted && issues.name ? (
                  <span role="alert" className="text-[13px] text-[var(--danger)]">{issues.name}</span>
                ) : (
                  <span className="text-[13px] text-[var(--ink-500)]">
                    Invitation Types use this name in their gate rules. It can&apos;t change after you create the gate.
                  </span>
                )}
              </>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="gate-code" className="text-sm font-semibold">Code</label>
            {isEdit ? (
              <Locked id="gate-code" value={form.code} mono />
            ) : (
              <>
                <input
                  id="gate-code"
                  placeholder="MAIN"
                  value={form.code}
                  onChange={(e) => { setServerCodeIssue(null); setForm((f) => ({ ...f, code: normalizeCode(e.target.value) })); }}
                  aria-invalid={(submitted && !!issues.code) || !!serverCodeIssue}
                  className={`${fieldBox} border-[var(--ink-300)] font-mono uppercase text-[var(--ink-900)]`}
                />
                {serverCodeIssue || (submitted && issues.code) ? (
                  <span role="alert" className="text-[13px] text-[var(--danger)]">{serverCodeIssue ?? issues.code}</span>
                ) : (
                  <span className="text-[13px] text-[var(--ink-500)]">
                    Letters and numbers, no spaces. It can&apos;t change after you create the gate.
                  </span>
                )}
              </>
            )}
          </div>

          {isEdit && (
            <div role="note" className="flex items-start gap-2.5 rounded-lg border border-solid border-[var(--warn-line)] bg-[var(--warn-bg-2)] px-3.5 py-3 text-sm leading-[1.45] text-[var(--warn-fg)]">
              <Lock size={18} className="mt-px shrink-0" aria-hidden />
              <span>Name and code are locked.{usage ? ` ${usage}` : ''}</span>
            </div>
          )}

          <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
            <legend className="mb-2 p-0 text-sm font-semibold">Type</legend>
            {KIND_GROUPS.map((group) => {
              const options = typeOptions.filter((t) => t.kind === group.id);
              if (options.length === 0) return null;
              return (
                <div key={group.id} role="group" aria-label={group.label} className="flex flex-col gap-2">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[15px] font-semibold">{group.label}</span>
                    <span className="text-[13px] text-[var(--ink-500)]">{group.description}</span>
                  </div>
                  <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(min(100%,180px),1fr))]">
                    {options.map((t) => {
                      const on = form.typeId === t.id;
                      return (
                        <label
                          key={t.id}
                          className={`flex cursor-pointer items-center gap-2.5 rounded-lg p-3.5 ${
                            on ? 'border-2 border-solid border-[var(--tenant-primary)] bg-[#F3F8F7]' : 'border border-solid border-[var(--line)] bg-white'
                          }`}
                        >
                          <input
                            type="radio"
                            name="gate-type"
                            checked={on}
                            onChange={() => setForm((f) => ({ ...f, typeId: t.id }))}
                            className="h-[18px] w-[18px] shrink-0 accent-[var(--tenant-primary)]"
                          />
                          <span className="text-[15px] font-semibold">{t.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {submitted && issues.typeId && <span role="alert" className="text-[13px] text-[var(--danger)]">{issues.typeId}</span>}
          </fieldset>

          {showZone && (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between gap-2">
                <label htmlFor="gate-zone" className="text-sm font-semibold">Zone</label>
                <Link to="/settings/zones" className="text-[13px] font-semibold text-[var(--tenant-primary)] no-underline">Manage zones</Link>
              </div>
              <span className="-mt-0.5 text-[13px] text-[var(--ink-500)]">The area inside the community this gate controls.</span>
              <select
                id="gate-zone"
                value={form.zoneId ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, zoneId: e.target.value ? Number(e.target.value) : null }))}
                className={`${fieldBox} border-[var(--ink-300)] bg-white text-[var(--ink-900)]`}
              >
                <option value="">Select a zone</option>
                {zones.filter((z) => typeof z.id === 'number').map((z) => (
                  <option key={z.id} value={z.id}>{z.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2.5 border-t border-solid border-[var(--line-subtle)] pt-4">
            <Link
              to="/settings/gates"
              className="inline-flex h-11 grow items-center justify-center rounded-lg border border-solid border-[var(--line)] px-[18px] text-sm font-semibold text-[var(--ink-700)] no-underline md:grow-0"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving || (isEdit && !dirty)}
              className="inline-flex h-11 grow items-center justify-center gap-2 rounded-lg border-0 bg-[var(--tenant-primary)] px-[18px] text-sm font-semibold text-white disabled:opacity-50 md:grow-0"
            >
              {saving && <Loader2 size={14} className="animate-spin" aria-hidden />}
              {isEdit ? 'Save changes' : 'Create gate'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};
